import { useState } from "react";
import {
  Paper,
  Text,
  Group,
  Badge,
  Stack,
  Progress,
  Collapse,
  UnstyledButton,
  SimpleGrid,
  Code,
  ScrollArea,
  ThemeIcon,
  Divider,
  Tooltip,
  Loader,
} from "@mantine/core";
import {
  IconChevronDown,
  IconChevronRight,
  IconCode,
  IconDatabase,
  IconAlertTriangle,
  IconTrash,
  IconCheck,
} from "@tabler/icons-react";
import { useGetTranslationCoverage } from "../../api/translations";
import dayjs from "dayjs";

function LanguageCoverageRow({ languageCode, data, codeKeys }) {
  const [opened, setOpened] = useState(false);

  const color =
    data.percentage === 100
      ? "green"
      : data.percentage >= 80
        ? "yellow"
        : "red";

  return (
    <Paper withBorder p="sm" radius="sm">
      <UnstyledButton
        onClick={() => setOpened((o) => !o)}
        w="100%"
        style={{ cursor: data.missingCount > 0 ? "pointer" : "default" }}
        disabled={data.missingCount === 0}
      >
        <Group justify="space-between" align="center">
          <Group gap="sm">
            {data.missingCount > 0 ? (
              opened ? (
                <IconChevronDown size={16} />
              ) : (
                <IconChevronRight size={16} />
              )
            ) : (
              <ThemeIcon size="sm" color="green" variant="light" radius="xl">
                <IconCheck size={12} />
              </ThemeIcon>
            )}
            <Text fw={600} tt="uppercase" size="sm">
              {languageCode}
            </Text>
          </Group>

          <Group gap="md">
            <Text size="sm" c="dimmed">
              {data.translatedKeys}/{codeKeys}
            </Text>
            <Badge color={color} variant="light" size="lg" radius="sm">
              {data.percentage}%
            </Badge>
          </Group>
        </Group>

        <Progress
          value={data.percentage}
          color={color}
          size="sm"
          mt="xs"
          radius="xl"
        />
      </UnstyledButton>

      {data.missingCount > 0 && (
        <Collapse expanded={opened}>
          <Divider my="xs" />
          <Text size="xs" c="dimmed" mb="xs">
            {data.missingCount} missing{" "}
            {data.missingCount === 1 ? "key" : "keys"}:
          </Text>
          <ScrollArea.Autosize mah={200}>
            <Stack gap={4}>
              {data.missingKeys.map((key) => (
                <Code key={key} fz="xs">
                  {key}
                </Code>
              ))}
            </Stack>
          </ScrollArea.Autosize>
        </Collapse>
      )}
    </Paper>
  );
}

function CoverageCard() {
  const { data: coverage, isLoading } = useGetTranslationCoverage();
  const [expanded, setExpanded] = useState(false);
  const [showUnused, setShowUnused] = useState(false);
  const [showMissing, setShowMissing] = useState(false);

  if (isLoading) {
    return (
      <Paper p="md" withBorder radius="md" mb="lg">
        <Group gap="xs">
          <Loader size="sm" />
          <Text size="sm" c="dimmed">
            Loading coverage data...
          </Text>
        </Group>
      </Paper>
    );
  }

  if (!coverage) return null;

  const hasMissingFromDb = coverage.missingFromDb?.length > 0;
  const hasUnusedInCode = coverage.unusedInCode?.length > 0;
  const languages = coverage.perLanguage
    ? Object.entries(coverage.perLanguage)
    : [];

  const issueCount =
    (coverage.missingFromDb?.length || 0) +
    (coverage.unusedInCode?.length || 0);

  return (
    <Paper p="md" withBorder radius="md" mb="lg">
      <UnstyledButton onClick={() => setExpanded((o) => !o)} w="100%">
        <Group justify="space-between" align="center">
          <Group gap="sm">
            {expanded ? (
              <IconChevronDown size={18} />
            ) : (
              <IconChevronRight size={18} />
            )}
            <Text size="lg" fw={700}>
              Translation Coverage
            </Text>
            {issueCount > 0 && (
              <Badge color="red" variant="light" size="sm" radius="sm">
                {issueCount} {issueCount === 1 ? "issue" : "issues"}
              </Badge>
            )}
            {issueCount === 0 && (
              <Badge color="green" variant="light" size="sm" radius="sm">
                All good
              </Badge>
            )}
          </Group>

          {coverage.generatedAt && (
            <Tooltip
              label={`Keys extracted from source code on ${dayjs(coverage.generatedAt).format("MMM D, YYYY [at] HH:mm")}`}
            >
              <Text size="xs" c="dimmed">
                Snapshot: {dayjs(coverage.generatedAt).format("MMM D, YYYY")}
              </Text>
            </Tooltip>
          )}
        </Group>
      </UnstyledButton>

      <Collapse expanded={expanded}>
        {/* Summary badges */}
        <SimpleGrid cols={{ base: 2, sm: 4 }} mb="md" mt="md">
          <Paper withBorder p="sm" radius="sm" bg="blue.0">
            <Group gap="xs" mb={4}>
              <ThemeIcon size="sm" color="blue" variant="light">
                <IconCode size={14} />
              </ThemeIcon>
              <Text size="xs" c="dimmed">
                Code Keys
              </Text>
            </Group>
            <Text fw={700} size="xl">
              {coverage.codeKeys}
            </Text>
          </Paper>

          <Paper withBorder p="sm" radius="sm" bg="violet.0">
            <Group gap="xs" mb={4}>
              <ThemeIcon size="sm" color="violet" variant="light">
                <IconDatabase size={14} />
              </ThemeIcon>
              <Text size="xs" c="dimmed">
                DB Keys
              </Text>
            </Group>
            <Text fw={700} size="xl">
              {coverage.dbKeys}
            </Text>
          </Paper>

          <Paper
            withBorder
            p="sm"
            radius="sm"
            bg={hasMissingFromDb ? "red.0" : "green.0"}
          >
            <Group gap="xs" mb={4}>
              <ThemeIcon
                size="sm"
                color={hasMissingFromDb ? "red" : "green"}
                variant="light"
              >
                <IconAlertTriangle size={14} />
              </ThemeIcon>
              <Text size="xs" c="dimmed">
                Missing from DB
              </Text>
            </Group>
            <Text fw={700} size="xl">
              {coverage.missingFromDb?.length || 0}
            </Text>
          </Paper>

          <Paper
            withBorder
            p="sm"
            radius="sm"
            bg={hasUnusedInCode ? "yellow.0" : "green.0"}
          >
            <Group gap="xs" mb={4}>
              <ThemeIcon
                size="sm"
                color={hasUnusedInCode ? "yellow" : "green"}
                variant="light"
              >
                <IconTrash size={14} />
              </ThemeIcon>
              <Text size="xs" c="dimmed">
                Unused in Code
              </Text>
            </Group>
            <Text fw={700} size="xl">
              {coverage.unusedInCode?.length || 0}
            </Text>
          </Paper>
        </SimpleGrid>

        {/* Missing from DB expandable */}
        {hasMissingFromDb && (
          <Paper withBorder p="sm" radius="sm" mb="sm" bg="red.0">
            <UnstyledButton onClick={() => setShowMissing((o) => !o)} w="100%">
              <Group gap="xs">
                {showMissing ? (
                  <IconChevronDown size={14} />
                ) : (
                  <IconChevronRight size={14} />
                )}
                <Text size="sm" fw={500} c="red.8">
                  {coverage.missingFromDb.length}{" "}
                  {coverage.missingFromDb.length === 1 ? "key" : "keys"} used in
                  code but missing from the database
                </Text>
              </Group>
            </UnstyledButton>
            <Collapse expanded={showMissing}>
              <ScrollArea.Autosize mah={200} mt="xs">
                <Stack gap={4}>
                  {coverage.missingFromDb.map((key) => (
                    <Code key={key} fz="xs" color="red.1">
                      {key}
                    </Code>
                  ))}
                </Stack>
              </ScrollArea.Autosize>
            </Collapse>
          </Paper>
        )}

        {/* Unused in code expandable */}
        {hasUnusedInCode && (
          <Paper withBorder p="sm" radius="sm" mb="sm" bg="yellow.0">
            <UnstyledButton onClick={() => setShowUnused((o) => !o)} w="100%">
              <Group gap="xs">
                {showUnused ? (
                  <IconChevronDown size={14} />
                ) : (
                  <IconChevronRight size={14} />
                )}
                <Text size="sm" fw={500} c="yellow.8">
                  {coverage.unusedInCode.length}{" "}
                  {coverage.unusedInCode.length === 1 ? "key" : "keys"} in
                  database but not referenced in code
                </Text>
              </Group>
            </UnstyledButton>
            <Collapse expanded={showUnused}>
              <ScrollArea.Autosize mah={200} mt="xs">
                <Stack gap={4}>
                  {coverage.unusedInCode.map((key) => (
                    <Code key={key} fz="xs" color="yellow.1">
                      {key}
                    </Code>
                  ))}
                </Stack>
              </ScrollArea.Autosize>
            </Collapse>
          </Paper>
        )}

        {/* Per-language breakdown */}
        <Text size="sm" fw={600} mb="xs" mt="md">
          Per-Language Coverage
        </Text>
        <Stack gap="sm">
          {languages.map(([lang, data]) => (
            <LanguageCoverageRow
              key={lang}
              languageCode={lang}
              data={data}
              codeKeys={coverage.codeKeys}
            />
          ))}
        </Stack>
      </Collapse>
    </Paper>
  );
}

export default CoverageCard;
